import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  Check, Loader2, Crown, ArrowLeft, 
  Zap, BarChart3, Bell, HeadphonesIcon, Shield, Clock, Sparkles, XCircle, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const Billing = () => {
  const { user, token, isPremium, refreshUser, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null); // Track which plan is loading
  const [plans, setPlans] = useState([]);
  const [freeFeatures, setFreeFeatures] = useState([]);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('pro_monthly');
  const [canceling, setCanceling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Check for success/cancel params
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');

    if (sessionId && success === 'true') {
      pollPaymentStatus(sessionId);
    } else if (canceled === 'true') {
      toast.info('Checkout was canceled');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Fetch plans
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/billing/plans`);
      setPlans(response.data.plans);
      setFreeFeatures(response.data.free_features);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.info('Payment verification is taking longer than expected. Your subscription will be activated shortly.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(
        `${API_BASE}/api/billing/checkout/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid' || response.data.status === 'complete') {
        toast.success('Welcome to Pro! Your subscription is now active.');
        await refreshUser();
        setCheckingPayment(false);
        navigate('/app/billing', { replace: true });
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(planId);

    try {
      const response = await axios.post(
        `${API_BASE}/api/billing/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.location.href = response.data.checkout_url;
    } catch (error) {
      const message = error.response?.data?.detail?.error || 'Failed to start checkout';
      toast.error(message);
      setLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/billing/subscription/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Subscription canceled');
      setShowCancelConfirm(false);
      await refreshUser();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to cancel subscription';
      toast.error(message);
    } finally {
      setCanceling(false);
    }
  };

  const proFeatures = [
    { icon: Zap, text: 'Unlimited market scans' },
    { icon: BarChart3, text: 'Unlimited AI article analysis' },
    { icon: Bell, text: 'Real-time anomaly alerts' },
    { icon: HeadphonesIcon, text: 'Priority support' },
    { icon: Shield, text: 'Advanced analytics dashboard' }
  ];

  // Find plans from API
  const monthlyPlan = plans.find(p => p.id === 'pro_monthly');
  const annualPlan = plans.find(p => p.id === 'pro_annual');

  return (
    <div 
      className="min-h-screen px-4 py-12"
      style={{
        background: 'linear-gradient(135deg, #0B0F1A 0%, #0E1022 50%, #12162B 100%)'
      }}
    >
      {/* Background glow */}
      <div 
        className="fixed inset-0 overflow-hidden pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(59, 130, 246, 0.08) 0%, transparent 60%)'
        }}
      />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Back button */}
        <Link 
          to="/app"
          className="inline-flex items-center gap-2 mb-8 text-sm transition-colors"
          style={{ color: '#71717A' }}
          onMouseEnter={(e) => e.target.style.color = '#A1A1AA'}
          onMouseLeave={(e) => e.target.style.color = '#71717A'}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <Crown className="w-8 h-8" style={{ color: '#3B82F6' }} />
          </div>
          <h1 
            className="text-4xl font-bold mb-3"
            style={{ color: '#F4F4F5' }}
          >
            {isPremium ? 'Your Pro Subscription' : 'Upgrade to Pro'}
          </h1>
          <p style={{ color: '#71717A' }} className="text-lg">
            {isPremium 
              ? 'You have full access to all premium features'
              : 'Choose the plan that works best for you'}
          </p>
        </div>

        {/* Loading state */}
        {checkingPayment && (
          <div 
            className="mb-8 p-4 rounded-xl flex items-center justify-center gap-3"
            style={{
              background: 'rgba(124, 92, 255, 0.1)',
              border: '1px solid rgba(124, 92, 255, 0.2)'
            }}
          >
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#7C5CFF' }} />
            <span style={{ color: '#A78BFA' }}>Verifying your payment...</span>
          </div>
        )}

        {/* Current Plan Status for Premium Users */}
        {isPremium ? (
          <>
            <div 
              className="rounded-2xl p-8 mb-8"
              style={{
                background: 'linear-gradient(135deg, rgba(124, 92, 255, 0.15) 0%, rgba(91, 140, 255, 0.1) 100%)',
                border: '1px solid rgba(124, 92, 255, 0.3)'
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #7C5CFF 0%, #5B8CFF 100%)' }}
                  >
                    <Crown className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold" style={{ color: '#F4F4F5' }}>Pro Plan</h2>
                    <p style={{ color: user?.cancels_at ? '#f59e0b' : '#4ade80' }} className="text-sm">
                      {user?.cancels_at 
                        ? `Cancels ${new Date(user.cancels_at).toLocaleDateString()}`
                        : 'Active'
                      }
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                {proFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <feature.icon className="w-5 h-5" style={{ color: '#7C5CFF' }} />
                    <span style={{ color: '#A1A1AA' }}>{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* Cancel Subscription Button */}
              {!user?.cancels_at && (
                <div className="pt-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                  <button
                    onClick={() => setShowCancelConfirm(true)}
                    className="text-sm flex items-center gap-2 transition-colors"
                    style={{ color: '#71717A' }}
                    onMouseEnter={(e) => e.target.style.color = '#ef4444'}
                    onMouseLeave={(e) => e.target.style.color = '#71717A'}
                    data-testid="cancel-subscription-btn"
                  >
                    <XCircle className="w-4 h-4" />
                    Cancel Subscription
                  </button>
                </div>
              )}
            </div>

            {/* Cancel Confirmation Dialog */}
            {showCancelConfirm && (
              <div 
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
                style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
              >
                <div 
                  className="w-full max-w-md rounded-2xl p-6"
                  style={{ 
                    background: '#18181b',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: 'rgba(239, 68, 68, 0.15)' }}
                    >
                      <AlertTriangle className="w-5 h-5" style={{ color: '#ef4444' }} />
                    </div>
                    <h3 className="text-lg font-semibold" style={{ color: '#FAFAFA' }}>
                      Cancel Subscription?
                    </h3>
                  </div>
                  
                  <p className="text-sm mb-6" style={{ color: '#A1A1AA' }}>
                    Your subscription will remain active until the end of your current billing period. 
                    After that, you will lose access to Pro features.
                  </p>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowCancelConfirm(false)}
                      className="flex-1 py-2.5 rounded-lg text-sm font-medium"
                      style={{ 
                        background: 'rgba(255,255,255,0.05)',
                        color: '#A1A1AA',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}
                    >
                      Keep Subscription
                    </button>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={canceling}
                      className="flex-1 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                      style={{ 
                        background: canceling ? 'rgba(239, 68, 68, 0.3)' : '#ef4444',
                        color: 'white'
                      }}
                      data-testid="confirm-cancel-btn"
                    >
                      {canceling ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Canceling...
                        </>
                      ) : (
                        'Yes, Cancel'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          /* Pricing Cards */
          <>
            {/* Plan Toggle / Cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {/* Free Plan */}
              <div 
                className="rounded-2xl p-6"
                style={{
                  background: 'rgba(20, 24, 40, 0.6)',
                  border: '1px solid rgba(113, 113, 122, 0.2)'
                }}
              >
                <h3 className="text-lg font-semibold mb-2" style={{ color: '#F4F4F5' }}>Free</h3>
                <p className="text-3xl font-bold mb-1" style={{ color: '#F4F4F5' }}>
                  $0 <span className="text-sm font-normal" style={{ color: '#71717A' }}>forever</span>
                </p>
                <p className="text-sm mb-6" style={{ color: '#71717A' }}>Basic access to market data</p>
                
                <div className="space-y-3">
                  {freeFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4" style={{ color: '#71717A' }} />
                      <span className="text-sm" style={{ color: '#A1A1AA' }}>{feature}</span>
                    </div>
                  ))}
                </div>

                <div 
                  className="mt-6 py-3 rounded-xl text-center text-sm"
                  style={{ 
                    background: 'rgba(113, 113, 122, 0.1)',
                    color: '#71717A'
                  }}
                >
                  Current Plan
                </div>
              </div>

              {/* Pro Monthly Plan */}
              <div 
                className="rounded-2xl p-6 relative"
                style={{
                  background: 'rgba(20, 24, 40, 0.8)',
                  border: selectedPlan === 'pro_monthly' 
                    ? '2px solid rgba(124, 92, 255, 0.5)' 
                    : '1px solid rgba(124, 92, 255, 0.2)',
                  boxShadow: selectedPlan === 'pro_monthly' ? '0 0 60px rgba(124, 92, 255, 0.15)' : 'none'
                }}
                onClick={() => setSelectedPlan('pro_monthly')}
              >
                {/* Trial badge */}
                <div 
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                  style={{
                    background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
                    color: 'white'
                  }}
                >
                  <Clock className="w-3 h-3" />
                  48-hour Free Trial
                </div>

                <h3 className="text-lg font-semibold mb-2 mt-2" style={{ color: '#F4F4F5' }}>Pro Monthly</h3>
                <p className="text-3xl font-bold mb-1" style={{ color: '#F4F4F5' }}>
                  $14.99 <span className="text-sm font-normal" style={{ color: '#71717A' }}>/month</span>
                </p>
                <p className="text-sm mb-6" style={{ color: '#71717A' }}>
                  {monthlyPlan?.description || 'Try free for 48 hours, then $14.99/month'}
                </p>
                
                <div className="space-y-3">
                  {proFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4" style={{ color: '#7C5CFF' }} />
                      <span className="text-sm" style={{ color: '#A1A1AA' }}>{feature.text}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); handleUpgrade('pro_monthly'); }}
                  disabled={loading === 'pro_monthly'}
                  className="mt-6 w-full py-3 rounded-xl text-white font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2"
                  style={{
                    background: loading === 'pro_monthly'
                      ? 'rgba(124, 92, 255, 0.5)' 
                      : 'linear-gradient(135deg, #7C5CFF 0%, #6B4FE0 100%)',
                    boxShadow: loading === 'pro_monthly'
                      ? 'none' 
                      : '0 4px 20px rgba(124, 92, 255, 0.3)'
                  }}
                  data-testid="upgrade-monthly-btn"
                >
                  {loading === 'pro_monthly' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting checkout...
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4" />
                      Start Free Trial
                    </>
                  )}
                </button>
                
                <p className="text-xs text-center mt-3" style={{ color: '#52525B' }}>
                  Cancel anytime during trial
                </p>
              </div>

              {/* Pro Annual Plan */}
              <div 
                className="rounded-2xl p-6 relative"
                style={{
                  background: 'rgba(20, 24, 40, 0.8)',
                  border: selectedPlan === 'pro_annual' 
                    ? '2px solid rgba(124, 92, 255, 0.5)' 
                    : '1px solid rgba(124, 92, 255, 0.2)',
                  boxShadow: selectedPlan === 'pro_annual' ? '0 0 60px rgba(124, 92, 255, 0.15)' : 'none'
                }}
                onClick={() => setSelectedPlan('pro_annual')}
              >
                {/* Best Value badge */}
                <div 
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    color: 'white'
                  }}
                >
                  <Sparkles className="w-3 h-3" />
                  Best Value - Save $30
                </div>

                <h3 className="text-lg font-semibold mb-2 mt-2" style={{ color: '#F4F4F5' }}>Pro Annual</h3>
                <p className="text-3xl font-bold mb-1" style={{ color: '#F4F4F5' }}>
                  $139.99 <span className="text-sm font-normal" style={{ color: '#71717A' }}>/year</span>
                </p>
                <p className="text-sm mb-1" style={{ color: '#71717A' }}>
                  {annualPlan?.description || 'Billed annually at $139.99 (save $40)'}
                </p>
                <p className="text-xs mb-6" style={{ color: '#4ade80' }}>
                  = $12.50/month
                </p>
                
                <div className="space-y-3">
                  {proFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="w-4 h-4" style={{ color: '#7C5CFF' }} />
                      <span className="text-sm" style={{ color: '#A1A1AA' }}>{feature.text}</span>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4" style={{ color: '#f59e0b' }} />
                    <span className="text-sm font-medium" style={{ color: '#f59e0b' }}>2 months FREE</span>
                  </div>
                </div>

                <button
                  onClick={(e) => { e.stopPropagation(); handleUpgrade('pro_annual'); }}
                  disabled={loading === 'pro_annual'}
                  className="mt-6 w-full py-3 rounded-xl text-white font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2"
                  style={{
                    background: loading === 'pro_annual'
                      ? 'rgba(245, 158, 11, 0.5)' 
                      : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    boxShadow: loading === 'pro_annual'
                      ? 'none' 
                      : '0 4px 20px rgba(245, 158, 11, 0.3)'
                  }}
                  data-testid="upgrade-annual-btn"
                >
                  {loading === 'pro_annual' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting checkout...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Subscribe & Save
                    </>
                  )}
                </button>
                
                <p className="text-xs text-center mt-3" style={{ color: '#52525B' }}>
                  Billed immediately, no trial
                </p>
              </div>
            </div>

            {/* Comparison note */}
            <div 
              className="text-center p-4 rounded-xl mb-8"
              style={{
                background: 'rgba(124, 92, 255, 0.05)',
                border: '1px solid rgba(124, 92, 255, 0.1)'
              }}
            >
              <p className="text-sm" style={{ color: '#A1A1AA' }}>
                <span style={{ color: '#4ade80' }}>Monthly:</span> Start with a 48-hour free trial. Cancel anytime.
                <span className="mx-3">•</span>
                <span style={{ color: '#f59e0b' }}>Annual:</span> Best value! Save $30/year with immediate access.
              </p>
            </div>
          </>
        )}

        {/* FAQ */}
        <div 
          className="rounded-2xl p-6"
          style={{
            background: 'rgba(20, 24, 40, 0.6)',
            border: '1px solid rgba(113, 113, 122, 0.15)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: '#F4F4F5' }}>Frequently Asked Questions</h3>
          <div className="space-y-4">
            <div>
              <p className="font-medium mb-1" style={{ color: '#A1A1AA' }}>How does the 48-hour free trial work?</p>
              <p className="text-sm" style={{ color: '#71717A' }}>
                The monthly plan includes a 48-hour free trial. Enter your payment method to start, and you won't be charged until after the trial. Cancel anytime during the trial period at no cost.
              </p>
            </div>
            <div>
              <p className="font-medium mb-1" style={{ color: '#A1A1AA' }}>Does the annual plan have a trial?</p>
              <p className="text-sm" style={{ color: '#71717A' }}>
                No, the annual plan is charged immediately but offers significant savings ($30/year off). If you're unsure, try the monthly plan with its free trial first.
              </p>
            </div>
            <div>
              <p className="font-medium mb-1" style={{ color: '#A1A1AA' }}>Can I cancel anytime?</p>
              <p className="text-sm" style={{ color: '#71717A' }}>
                Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
              </p>
            </div>
            <div>
              <p className="font-medium mb-1" style={{ color: '#A1A1AA' }}>What payment methods are accepted?</p>
              <p className="text-sm" style={{ color: '#71717A' }}>
                We accept all major credit cards through our secure payment processor, Stripe.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
